<template>
  <div class="pre-invoice-page">
    <div class="ts-flex ts-justify-between ts-items-center ts-mb-3">
      <div>
        <h1 class="page-header ts-mb-0">{{ $t('preInvoiceV2.pageTitle') }}</h1>
      </div>
      <ts-breadcrumb :items="[
        { text: $t('toHomePage'), href: '/' },
        { text: $t('preInvoiceV2.pageTitle'), href: '/v2/payment/pre-invoices' },
        { text: $t('preInvoiceV2.edit'), active: true }
      ]" />
    </div>

    <ts-loading-banner :loading="loading">
      <div class="pre-invoice-shell">
        <div class="pre-invoice-top-grid">
          <student-summary-section
            :model="model"
            :selected-enrollment="selectedEnrollment"
            :enrollments="enrollments"
            :loading-enrollments="loadingEnrollments"
            :is-edit="isEdit"
            @student-type-change="onStudentTypeChange"
            @search-enrollments="loadEnrollmentsBySearch"
            @select-enrollment="selectEnrollmentById"
            @clear-selected="clearSelectedEnrollment"
          />

          <payment-info-section
            :model="model"
            :formatted-total="formatAmount(totalAmount)"
            @invoice-date-change="onPreInvoiceDateChange"
          />
        </div>

        <academic-info-section
          :model="model"
          :target-reason="targetReason"
          :main-programs="mainPrograms"
          :sub-programs="subPrograms"
          :academic-years="academicYears"
          :academic-terms="academicTerms"
          :grades="grades"
          :study-times="studyTimes"
          :current-academic-term-id="currentAcademicTermId"
          @main-program-change="onMainProgramChange"
          @sub-program-change="onSubProgramChange"
          @academic-year-change="onAcademicYearChange"
          @academic-term-change="onAcademicTermChange"
          @grade-change="onGradeChange"
          @study-time-change="onStudyTimeChange"
        />

        <fee-items-section
          :model="model"
          :can-load-fees="canLoadFees"
          :formatted-total="formatAmount(totalAmount)"
          :fee-types="feeTypes"
          :errors="errors"
          :term-start-date="selectedTermStartDate"
          @add-item="addItem"
          @fee-type-change="onFeeTypeChange"
          @fee-item-change="onFeeItemChange"
          @date-range-change="recalculateItemDateRange"
          @recalculate-item="recalculateItem"
          @sync-total="syncTotal"
          @remove-item="removeItem"
        />

        <remark-section :model="model" />
      </div>
    </ts-loading-banner>

    <action-footer
      :model="model"
      :formatted-total="formatAmount(totalAmount)"
      :saving="saving"
      :saving-action="savingAction"
      :busy="loading || isKhqrPending"
      :show-pay-by-qr="canPayByQr"
      :qr-busy="khqrBusy"
      :qr-disabled="isKhqrPending || totalAmount <= 0"
      :show-save-and-add-new="!khqrSourceId"
      :save-disabled="isKhqrPending"
      :is-edit="isEdit"
      @cancel="cancel"
      @reset="resetForm"
      @save="save"
      @pay-by-qr="openPayByQr"
    />

    <pre-invoice-khqr-payment-modal
      :visible="khqrModalVisible"
      :source-id="khqrSourceId"
      :amount="totalAmount"
      :invoice-date="model.pre_inv_date"
      :bank-accounts="khqrBankAccounts"
      :resolve-source-id="resolveKhqrSourceId"
      @update:visible="khqrModalVisible = $event"
      @close="khqrModalVisible = false"
      @status-change="onKhqrStatusChange"
      @display-change="onKhqrDisplayChange"
      @payment-success="onKhqrPaymentSuccess"
    />


    <a-modal
      :visible="exchangeRateModalVisible"
      centered
      :footer="null"
      :closable="true"
      :maskClosable="false"
      :zIndex="2100"
      wrap-class-name="exchange-rate-alert-wrap"
      width="420px"
      @cancel="closeExchangeRateModal"
    >
      <div class="exchange-rate-alert">
        <div class="exchange-rate-alert-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <h3>{{ $t('preInvoiceV2.exchangeRateRequiredTitle') }}</h3>
        <p>{{ $t('preInvoiceV2.exchangeRateRequiredDesc', { date: model.pre_inv_date || '-' }) }}</p>
      </div>
    </a-modal>

    <pdf-preview-modal
      :visible="pdfVisible"
      :title="$t('preInvoiceV2.previewPdf')"
      :src="pdfSrc"
      :loading="pdfLoading"
      :z-index="2000"
      auto-print
      @close="closePdf"
      @update:visible="pdfVisible = $event"
    />
  </div>
</template>

<script>
import moment from 'moment'
import notificationAlert from '@/utils/NotificationAlert'
import { Errors } from 'form-backend-validation'
import { mapActions } from 'vuex'
import StudentSummarySection from '../components/StudentSummarySection.vue'
import PaymentInfoSection from '../components/PaymentInfoSection.vue'
import AcademicInfoSection from '../components/AcademicInfoSection.vue'
import FeeItemsSection from '../components/FeeItemsSection.vue'
import RemarkSection from '../components/RemarkSection.vue'
import ActionFooter from '../components/ActionFooter.vue'
import PreInvoiceKhqrPaymentModal from '../components/PreInvoiceKhqrPaymentModal.vue'
import PdfPreviewModal from '../components/PdfPreviewModal.vue'
import customerDisplayMixin from '@/mixins/pre-invoice-v2/customerDisplayMixin'
import pdfPreviewMixin from '@/mixins/pre-invoice-v2/pdfPreviewMixin'

export default {
  mixins: [customerDisplayMixin, pdfPreviewMixin],
  name: 'PreInvoiceV2Edit',
  components: {
    StudentSummarySection,
    PaymentInfoSection,
    AcademicInfoSection,
    FeeItemsSection,
    RemarkSection,
    ActionFooter,
    PreInvoiceKhqrPaymentModal,
    PdfPreviewModal
  },
  data() {
    return {
      loadingCount: 0,
      saving: false,
      savingAction: null,
      loadingEnrollments: false,
      khqrModalVisible: false,
      exchangeRateModalVisible: false,
      khqrBusy: false,
      khqrSourceId: null,
      khqrStatus: null,
      khqrDisplay: null,
      khqrBankAccounts: [],
      paymentLocked: false,
      errors: new Errors(),
      studentSearch: '',
      selectedEnrollment: null,
      enrollments: [],
      mainPrograms: [],
      subPrograms: [],
      academicYears: [],
      academicTerms: [],
      grades: [],
      studyTimes: [],
      feeTypes: [],
      exchangeRateSetup: null,
      targetReason: null,
      model: this.freshModel()
    }
  },
  computed: {
    loading() {
      return this.loadingCount > 0 || this.saving
    },
    selectedTerm() {
      return this.academicTerms.find(item => item.ac_term_id === this.model.ac_term_id)
    },
    currentAcademicTermId() {
      return this.selectedEnrollment ? this.toNumber(this.selectedEnrollment.ac_term_id) : null
    },
    selectedTermStartDate() {
      return this.selectedTerm && this.selectedTerm.term_start
        ? moment(this.selectedTerm.term_start).format('DD-MM-YYYY')
        : null
    },
    canLoadFees() {
      return Boolean(this.model.acps_id && this.model.ac_term_id && this.model.grade_id && (this.model.student_type === 'fresh' || this.model.student_id))
    },
    totalAmount() {
      return this.model.items.reduce((total, item) => total + Number(item.amount_include_vat || 0), 0)
    },
    canPayByQr() {
      return this.totalAmount > 0 && this.khqrBankAccounts.length > 0 && !this.isKhqrLocked
    },
    isKhqrPending() {
      return this.khqrStatus === 'pending'
    },
    isKhqrLocked() {
      return this.paymentLocked
    },
    isEdit() {
      return true
    },
    invoiceId() {
      return this.$route.params.id || this.$route.query.id || null
    }
  },
  async mounted() {
    if (this.redirectIfUnauthorized()) return
    this.startCustomerDisplaySession()
    await Promise.all([
      this.loadInitialOptions(),
      this.loadEnrollments()
    ])
    if (this.isEdit) {
      await this.loadEditRecord()
    }
    this.markCustomerDisplayReady()
  },
  beforeRouteLeave(to, from, next) {
    if (this.isKhqrPending) {
      next(false)
      return
    }
    next()
  },
  methods: {

    can(permission) {
      return this.$store.getters.authPermissionAllow(permission)
    },
    redirectIfUnauthorized() {
      if (!this.can('update-pre-invoices')) {
        this.$router.replace({ name: 'pre-invoice-v2' })
        return true
      }
      return false
    },
    ...mapActions('preInvoiceV2', [
      'searchEnrollments',
      'getMainPrograms',
      'getSubPrograms',
      'getAcademicYears',
      'getAcademicTerms',
      'getGrades',
      'getStudyTimes',
      'getAcademicTarget',
      'getFeeTypes',
      'getFeeItems',
      'getFeeDateRange',
      'getFeeDiscount',
      'getExchangeRate',
      'getKhqrBankAccounts',
      'getDetail',
      'onUpdate',
      'previewPdf',
      'previewInvoicePdf'
    ]),
    toNumber(value) {
      return value === null || value === undefined || value === '' ? null : Number(value)
    },
    async withPageLoading(callback) {
      this.loadingCount += 1
      try {
        return await callback()
      } finally {
        this.loadingCount = Math.max(this.loadingCount - 1, 0)
      }
    },
    normalizeOptions(rows, keys) {
      return (rows || []).map(row => {
        const item = Object.assign({}, row)
        keys.forEach(key => {
          item[key] = this.toNumber(item[key])
        })
        return item
      })
    },
    async loadEditRecord() {
      return this.withPageLoading(async () => {
        try {
          const response = await this.getDetail(this.invoiceId)
          const record = response.data

          this.paymentLocked = record.is_paid === true || record.is_paid === 1 || Number(record.amount_paid || 0) > 0
          this.khqrSourceId = this.invoiceId

          this.model = Object.assign(this.freshModel(), {
            student_type: 'old',
            student_id: this.toNumber(record.student_id),
            enrollment_id: null,
            pre_inv_date: record.pre_invoice_date,
            exchange_rate: record.pre_khr_exchange_rate,
            preview_pdf: false,
            ac_year_id: this.toNumber(record.ac_year_id),
            acp_id: this.toNumber(record.acp_id),
            acps_id: this.toNumber(record.acps_id),
            ac_term_id: this.toNumber(record.ac_term_id),
            grade_id: this.toNumber(record.grade_id),
            class_id: this.toNumber(record.class_id),
            study_time_id: this.studyTimeIdFromText(record.study_time),
            study_time: record.study_time,
            total_amount: Number(record.total_amount || 0),
            remark: record.pre_invoice_remark || record.remark || null,
            vehicle_id: record.vehicle_id || null,
            items: []
          })

          this.selectedEnrollment = this.buildSelectedEnrollment(record)
          await Promise.all([
            this.loadSubPrograms(this.model.acp_id),
            this.loadTerms(this.model.acps_id, this.model.ac_year_id, this.model.ac_term_id),
            this.loadGrades(this.model.acps_id, this.model.grade_id)
          ])
          await this.reloadFeeTypes()
          this.model.items = this.hydrateEditItems(record.items || [])
          await Promise.all(this.model.items.map((item, index) => this.loadFeeOptionsForItem(index)))
        } catch (error) {
          notificationAlert(error)
          this.$router.push({ name: 'pre-invoice-v2' })
        }
      })
    },
    buildSelectedEnrollment(record) {
      return {
        enrollment_id: null,
        student_id: this.toNumber(record.student_id),
        card_number: record.card_number,
        first_name_en: record.first_name_en,
        last_name_en: record.last_name_en,
        first_name_kh: record.first_name_kh,
        last_name_kh: record.last_name_kh,
        gender: record.gender,
        phone: record.phone,
        dob: record.dob,
        photo: record.photo,
        acp_id: this.toNumber(record.acp_id),
        acps_id: this.toNumber(record.acps_id),
        ac_year_id: this.toNumber(record.ac_year_id),
        ac_term_id: this.toNumber(record.ac_term_id),
        grade_id: this.toNumber(record.grade_id),
        class_id: this.toNumber(record.class_id),
        study_time_id: this.studyTimeIdFromText(record.study_time),
        study_time: record.study_time,
        acp_name_foreign: record.acp_name_foreign,
        acps_name_foreign: record.acps_name_foreign,
        term_name: record.term_name,
        grade_name_foreign: record.grade_name_foreign,
        class_name: record.class_name,
        ac_status_id: record.ac_status_id
      }
    },
    hydrateEditItems(rows) {
      return rows.map(row => Object.assign(this.emptyItem(), {
        fee_type_id: this.toNumber(row.fee_type_id),
        fee_setup_id: this.toNumber(row.fee_setup_id),
        fee_description: row.fee_description || row.fee_name,
        quantity: Number(row.quantity || 1),
        fee_start_date: row.fee_start_date,
        fee_end_date: row.fee_end_date,
        payment_term_id: this.toNumber(row.payment_term_id),
        duration_in_month: Number(row.duration_in_month || 0),
        fee_amount: Number(row.fee_amount || 0),
        discount_type: row.discount_type || '%',
        discount_rate: Number(row.discount_rate || 0),
        discount_amount: Number(row.discount_amount || 0),
        discount_reason: row.discount_reason || null,
        total_amount: Number(row.total_amount || 0),
        vat_rate: Number(row.vat_rate || 0),
        vat_amount: Number(row.vat_amount || 0),
        amount_include_vat: Number(row.amount_include_vat || 0),
        ac_term_id: this.toNumber(row.ac_term_id) || this.model.ac_term_id,
        grade_id: this.toNumber(row.grade_id) || this.model.grade_id,
        shift_id: this.toNumber(row.shift_id),
        payment_term_name: row.payment_term_name || row.setup_payment_term_name || null,
        pro_type_id: this.toNumber(row.pro_type_id),
        feeItems: []
      }))
    },
    async loadFeeOptionsForItem(index) {
      const item = this.model.items[index]
      if (!item || !item.fee_type_id) return
      try {
        const response = await this.getFeeItems({
          fee_type_id: item.fee_type_id,
          grade_id: this.model.grade_id
        })
        item.feeItems = this.normalizeOptions(response.data, [
          'fee_type_id',
          'fee_setup_id',
          'payment_term_id',
          'duration_in_month',
          'shift_id',
          'grade_id'
        ])
        const hasCurrentFee = item.feeItems.some(fee => String(fee.fee_setup_id) === String(item.fee_setup_id))
        if (item.fee_setup_id && !hasCurrentFee) {
          item.feeItems.unshift({
            fee_type_id: item.fee_type_id,
            fee_setup_id: item.fee_setup_id,
            fee_description: item.fee_description,
            fee_amount: item.fee_amount,
            payment_term_id: item.payment_term_id,
            payment_term_name: item.payment_term_name,
            duration_in_month: item.duration_in_month,
            shift_id: item.shift_id,
            grade_id: item.grade_id
          })
        }
      } catch (error) {
        notificationAlert(error)
      }
    },
    freshModel() {
      return {
        student_type: 'old',
        student_id: null,
        enrollment_id: null,
        pre_inv_date: moment().format('DD-MM-YYYY'),
        exchange_rate: null,
        preview_pdf: false,
        ac_year_id: null,
        acp_id: null,
        acps_id: null,
        ac_term_id: null,
        grade_id: null,
        class_id: null,
        study_time_id: null,
        study_time: null,
        total_amount: 0,
        remark: null,
        vehicle_id: null,
        student: {
          first_name_en: null,
          last_name_en: null,
          first_name_kh: null,
          last_name_kh: null,
          date_of_birth: null,
          gender: null
        },
        items: []
      }
    },
    async loadInitialOptions() {
      return this.withPageLoading(async () => {
        try {
          const [mainPrograms, academicYears, studyTimes, exchangeRate, khqrAccounts] = await Promise.all([
            this.getMainPrograms(),
            this.getAcademicYears(),
            this.getStudyTimes(),
            this.getExchangeRate({ pre_inv_date: this.model.pre_inv_date }),
            this.getKhqrBankAccounts()
          ])
          this.mainPrograms = this.normalizeOptions(mainPrograms.data, ['acp_id'])
          this.academicYears = this.normalizeOptions(academicYears.data, ['ac_year_id', 'ac_status_id'])
          this.studyTimes = this.normalizeOptions(studyTimes.data, ['ac_st_id'])
          this.khqrBankAccounts = khqrAccounts.data.data || []
          this.applyExchangeRateSetup(exchangeRate.data)
        } catch (error) {
          notificationAlert(error)
        }
      })
    },
    applyExchangeRateSetup(rate) {
      if (!rate || Array.isArray(rate) || !rate.exchange_rate) {
        this.exchangeRateSetup = null
        this.model.exchange_rate = null
        this.showExchangeRateMissingModal()
        return false
      }

      this.exchangeRateSetup = rate
      this.model.exchange_rate = rate.exchange_rate
      return true
    },
    hasValidExchangeRateSetup() {
      if (!this.exchangeRateSetup || !this.exchangeRateSetup.exchange_rate || !this.model.pre_inv_date) {
        return false
      }

      const selectedDate = moment(this.model.pre_inv_date, 'DD-MM-YYYY').format('YYYY-MM-DD')
      const setupDate = moment(this.exchangeRateSetup.value_date).format('YYYY-MM-DD')
      return selectedDate === setupDate
    },
    ensureExchangeRateSetup() {
      if (this.hasValidExchangeRateSetup()) {
        return true
      }

      this.showExchangeRateMissingModal()
      return false
    },
    showExchangeRateMissingModal() {
      this.exchangeRateModalVisible = true
    },
    closeExchangeRateModal() {
      this.exchangeRateModalVisible = false
    },
    async onStudentTypeChange() {
      if (this.isEdit) {
        this.model.student_type = 'old'
        return
      }
      const nextType = this.model.student_type
      this.resetForm(nextType, false)
      await this.loadInitialOptions()
      if (nextType === 'old') {
        await this.loadEnrollments()
      }
    },
    async onPreInvoiceDateChange() {
      await this.loadExchangeRate()
    },
    async loadExchangeRate() {
      if (!this.model.pre_inv_date) return
      return this.withPageLoading(async () => {
        try {
          const response = await this.getExchangeRate({ pre_inv_date: this.model.pre_inv_date })
          this.applyExchangeRateSetup(response.data)
        } catch (error) {
          this.model.exchange_rate = null
          this.exchangeRateSetup = null
          notificationAlert(error)
        }
      })
    },
    async loadEnrollmentsBySearch(value) {
      this.studentSearch = value
      await this.loadEnrollments()
    },
    async loadEnrollments() {
      this.loadingEnrollments = true
      return this.withPageLoading(async () => {
        try {
          const response = await this.searchEnrollments({
            search: this.studentSearch,
            perPage: 10
          })
          this.enrollments = this.normalizeOptions(response.data.data || [], [
            'enrollment_id',
            'student_id',
            'class_id',
            'acp_id',
            'acps_id',
            'ac_year_id',
            'ac_term_id',
            'grade_id',
            'teacher_resp_id',
            'ac_status_id',
            'study_time_id'
          ])
        } catch (error) {
          notificationAlert(error)
        } finally {
          this.loadingEnrollments = false
        }
      })
    },
    async selectEnrollmentById(enrollmentId) {
      const record = this.enrollments.find(item => String(item.enrollment_id) === String(enrollmentId))
      if (record) {
        await this.selectEnrollment(record)
      }
    },
    async selectEnrollment(record) {
      this.selectedEnrollment = record
      this.model.student_id = this.toNumber(record.student_id)
      this.model.enrollment_id = this.toNumber(record.enrollment_id)
      this.model.class_id = this.toNumber(record.class_id)
      this.model.acp_id = this.toNumber(record.acp_id)
      this.model.acps_id = this.toNumber(record.acps_id)
      this.model.study_time_id = this.toNumber(record.study_time_id) || this.studyTimeIdFromText(record.study_time)
      this.model.study_time = this.studyTimeNameFromId(this.model.study_time_id) || record.study_time
      await Promise.all([
        this.loadSubPrograms(record.acp_id),
        this.loadTerms(record.acps_id, record.ac_year_id, record.ac_term_id),
        this.loadGrades(record.acps_id, record.grade_id)
      ])
      await this.loadAcademicTarget(record.enrollment_id)
    },
    clearSelectedEnrollment() {
      this.selectedEnrollment = null
      this.model.student_id = null
      this.model.enrollment_id = null
      this.model.class_id = null
      this.model.acp_id = null
      this.model.acps_id = null
      this.model.ac_year_id = null
      this.model.ac_term_id = null
      this.model.grade_id = null
      this.model.study_time_id = null
      this.model.study_time = null
      this.model.items = []
      this.feeTypes = []
      this.targetReason = null
    },
    formatAmount(value) {
      return `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    async loadAcademicTarget(enrollmentId) {
      return this.withPageLoading(async () => {
        try {
          const response = await this.getAcademicTarget({ enrollment_id: enrollmentId })
          const target = response.data
          this.model.acp_id = this.toNumber(target.acp_id || this.model.acp_id)
          this.model.acps_id = this.toNumber(target.acps_id || this.model.acps_id)
          this.model.ac_year_id = this.toNumber(target.ac_year_id)
          this.model.ac_term_id = this.toNumber(target.ac_term_id)
          this.model.grade_id = this.toNumber(target.grade_id)
          this.model.class_id = this.toNumber(target.class_id)
          this.model.study_time_id = this.toNumber(target.study_time_id) || this.studyTimeIdFromText(target.study_time)
          this.model.study_time = this.studyTimeNameFromId(this.model.study_time_id) || target.study_time
          await Promise.all([
            this.loadTerms(
              this.model.acps_id,
              this.model.ac_year_id,
              this.selectedEnrollment ? this.selectedEnrollment.ac_term_id : null
            ),
            this.loadGrades(
              this.model.acps_id,
              this.selectedEnrollment ? this.selectedEnrollment.grade_id : null
            )
          ])
          this.targetReason = target.is_promoted_grade
            ? this.$t('preInvoiceV2.promoted')
            : this.$t('preInvoiceV2.sameGrade')
          await this.reloadFeeTypes()
        } catch (error) {
          notificationAlert(error)
        }
      })
    },
    async onMainProgramChange(value) {
      if (this.model.student_type === 'old') return
      this.model.acps_id = null
      this.model.ac_term_id = null
      this.model.grade_id = null
      this.model.items = []
      this.feeTypes = []
      await this.loadSubPrograms(value)
    },
    async onSubProgramChange(value) {
      if (this.model.student_type === 'old') return
      this.model.ac_term_id = null
      this.model.grade_id = null
      this.model.items = []
      this.feeTypes = []
      await Promise.all([
        this.loadTerms(value, this.model.ac_year_id),
        this.loadGrades(value)
      ])
    },
    async onAcademicYearChange(value) {
      if (this.model.student_type === 'old') return
      this.model.ac_term_id = null
      this.model.items = []
      this.feeTypes = []
      await this.loadTerms(this.model.acps_id, value)
    },
    async onAcademicTermChange() {
      this.model.items = []
      await this.reloadFeeTypes()
    },
    async onGradeChange() {
      this.model.items = []
      await this.reloadFeeTypes()
    },
    onStudyTimeChange(value) {
      this.model.study_time = this.studyTimeNameFromId(value)
    },
    studyTimeNameFromId(value) {
      const item = this.studyTimes.find(row => Number(row.ac_st_id) === Number(value))
      return item ? item.study_time : null
    },
    studyTimeIdFromText(value) {
      const item = this.studyTimes.find(row => String(row.study_time || '').trim() === String(value || '').trim())
      return item ? this.toNumber(item.ac_st_id) : null
    },
    async loadSubPrograms(acpId) {
      if (!acpId) return
      return this.withPageLoading(async () => {
        const response = await this.getSubPrograms({ acp_id: acpId })
        this.subPrograms = this.normalizeOptions(response.data, ['acps_id', 'acp_id'])
      })
    },
    async loadTerms(acpsId, acYearId, minAcTermId = null) {
      if (!acpsId) return
      return this.withPageLoading(async () => {
        const response = await this.getAcademicTerms({
          acps_id: acpsId,
          ac_year_id: acYearId,
          min_ac_term_id: minAcTermId
        })
        this.academicTerms = this.normalizeOptions(response.data, ['ac_term_id', 'ac_year_id', 'acps_id', 'term_order'])
      })
    },
    async loadGrades(acpsId, currentGradeId = null) {
      if (!acpsId) return
      return this.withPageLoading(async () => {
        const response = await this.getGrades({
          acps_id: acpsId,
          current_grade_id: currentGradeId
        })
        this.grades = this.normalizeOptions(response.data, ['grade_id', 'next_grade_id'])
      })
    },
    async reloadFeeTypes() {
      if (!this.canLoadFees) return
      return this.withPageLoading(async () => {
        try {
          const response = await this.getFeeTypes({
            student_id: this.model.student_id || 0,
            acps_id: this.model.acps_id,
            grade_id: this.model.grade_id,
            ac_term_id: this.model.ac_term_id,
            exclude_inv_id: this.isEdit ? this.invoiceId : null
          })
          this.feeTypes = this.normalizeOptions(response.data, ['fee_type_id'])
        } catch (error) {
          notificationAlert(error)
        }
      })
    },
    addItem() {
      if (!this.canLoadFees) {
        this.$notification.error({
          message: this.$t('preInvoiceV2.error'),
          description: this.$t(this.model.student_type === 'old' ? 'preInvoiceV2.requiredStudent' : 'preInvoiceV2.requiredAcademic'),
          style: { zIndex: 1031 }
        })
        return
      }
      this.model.items.push(this.emptyItem())
    },
    async autoAddDefaultFeeItems() {
      const defaultFeeTypeIds = [1, 4]
      for (const feeTypeId of defaultFeeTypeIds) {
        const feeType = this.feeTypes.find(type => Number(type.fee_type_id) === feeTypeId)
        const exists = this.model.items.some(item => Number(item.fee_type_id) === feeTypeId)
        if (!feeType || exists) continue

        const item = this.emptyItem()
        item.fee_type_id = feeTypeId
        this.model.items.push(item)
        const index = this.model.items.length - 1
        await this.onFeeTypeChange(index)

        const firstFeeItem = this.model.items[index].feeItems[0]
        if (!firstFeeItem) continue

        this.model.items[index].fee_setup_id = firstFeeItem.fee_setup_id
        await this.onFeeItemChange(index)
      }
    },
    emptyItem() {
      return {
        fee_type_id: null,
        fee_setup_id: null,
        fee_description: null,
        quantity: 1,
        fee_start_date: null,
        fee_end_date: null,
        payment_term_id: null,
        duration_in_month: 0,
        fee_amount: 0,
        discount_type: '%',
        discount_rate: 0,
        discount_amount: 0,
        discount_reason: null,
        discount_source: null,
        total_amount: 0,
        vat_rate: 0,
        vat_amount: 0,
        amount_include_vat: 0,
        ac_term_id: this.model.ac_term_id,
        grade_id: this.model.grade_id,
        shift_id: null,
        payment_term_name: null,
        min_fee_start_date: null,
        pro_type_id: null,
        feeItems: []
      }
    },
    async onFeeTypeChange(index) {
      const item = this.model.items[index]
      item.fee_setup_id = null
      item.fee_description = null
      item.fee_amount = 0
      item.discount_type = '%'
      item.discount_rate = 0
      item.discount_amount = 0
      item.discount_reason = null
      item.discount_source = null
      item.total_amount = 0
      item.amount_include_vat = 0
      item.feeItems = []
      if (!item.fee_type_id) return
      return this.withPageLoading(async () => {
        try {
          const response = await this.getFeeItems({
            fee_type_id: item.fee_type_id,
            grade_id: this.model.grade_id
          })
          item.feeItems = this.normalizeOptions(response.data, [
            'fee_type_id',
            'fee_setup_id',
            'payment_term_id',
            'duration_in_month',
            'shift_id',
            'grade_id'
          ])
        } catch (error) {
          notificationAlert(error)
        }
      })
    },
    async onFeeItemChange(index) {
      const item = this.model.items[index]
      const fee = item.feeItems.find(row => String(row.fee_setup_id) === String(item.fee_setup_id))
      if (!fee) return
      item.fee_description = fee.fee_description
      item.fee_amount = Number(fee.fee_amount || 0)
      item.payment_term_id = fee.payment_term_id
      item.payment_term_name = fee.payment_term_name
      item.duration_in_month = Number(fee.duration_in_month || 0)
      item.shift_id = fee.shift_id
      item.discount_type = '%'
      item.discount_rate = 0
      item.discount_amount = 0
      item.discount_reason = null
      item.discount_source = null
      item.fee_start_date = null
      item.fee_end_date = null

      if (item.payment_term_id && item.duration_in_month) {
        item.fee_start_date = this.cascadeStartDateForIndex(index) || item.fee_start_date
        await this.loadFeeDateRange(index)
      }

      await this.loadFeeDiscount(index)
      this.recalculateItem(index)
      await this.cascadeFeeDateRanges(index)
    },
    async loadFeeDateRange(index) {
      const item = this.model.items[index]
      return this.withPageLoading(async () => {
        try {
          const response = await this.getFeeDateRange({
            ac_term_id: this.model.ac_term_id,
            fee_setup_id: item.fee_setup_id,
            student_id: this.model.student_id || 0,
            acps_id: this.model.acps_id,
            grade_id: this.model.grade_id,
            fee_start_date: item.fee_start_date,
            quantity: item.quantity || 1
          })
          item.payment_term_id = response.data.payment_term_id
          item.payment_term_name = response.data.payment_term_name
          item.duration_in_month = Number(response.data.duration_in_month || 0)
          item.min_fee_start_date = response.data.min_fee_start_date || response.data.fee_start_date
          item.fee_start_date = response.data.fee_start_date
          item.fee_end_date = response.data.fee_end_date
        } catch (error) {
          notificationAlert(error)
        }
      })
    },
    async recalculateItemDateRange(index) {
      const item = this.model.items[index]
      if (item.payment_term_id && item.duration_in_month) {
        await this.loadFeeDateRange(index)
      }
      this.recalculateItem(index)
      await this.cascadeFeeDateRanges(index)
    },
    cascadeStartDateForIndex(index) {
      const item = this.model.items[index]
      if (!item || !item.fee_type_id) return null

      for (let i = index - 1; i >= 0; i--) {
        const previous = this.model.items[i]
        if (
          String(previous.fee_type_id || '') === String(item.fee_type_id || '') &&
          previous.fee_end_date
        ) {
          return moment(previous.fee_end_date, 'DD-MM-YYYY').add(1, 'day').format('DD-MM-YYYY')
        }
      }

      return null
    },
    async cascadeFeeDateRanges(index) {
      const source = this.model.items[index]
      if (!source || !source.fee_type_id || !source.fee_end_date) return

      let previousEndDate = source.fee_end_date
      for (let i = index + 1; i < this.model.items.length; i++) {
        const item = this.model.items[i]
        if (String(item.fee_type_id || '') !== String(source.fee_type_id || '')) {
          continue
        }
        if (!item.payment_term_id || !item.duration_in_month || !item.fee_setup_id) {
          continue
        }

        const nextStartDate = moment(previousEndDate, 'DD-MM-YYYY').add(1, 'day').format('DD-MM-YYYY')
        if (item.fee_start_date !== nextStartDate) {
          item.fee_start_date = nextStartDate
        }
        await this.loadFeeDateRange(i)
        this.recalculateItem(i)
        previousEndDate = item.fee_end_date || previousEndDate
      }
    },
    async loadFeeDiscount(index) {
      const item = this.model.items[index]
      if (Number(item.fee_type_id) !== 1) return
      return this.withPageLoading(async () => {
        try {
          const response = await this.getFeeDiscount({
            fee_type_id: item.fee_type_id,
            student_id: this.model.student_id || 0,
            acps_id: this.model.acps_id,
            ac_term_id: this.model.ac_term_id,
            pre_inv_date: this.model.pre_inv_date
          })
          item.discount_type = response.data.discount_type || '%'
          item.discount_rate = Number(response.data.discount_rate || 0)
          item.discount_amount = Number(response.data.discount_amount || 0)
          item.discount_reason = response.data.discount_reason || null
          item.discount_source = response.data.discount_source || null
        } catch (error) {
          notificationAlert(error)
        }
      })
    },
    recalculateItem(index) {
      const item = this.model.items[index]
      const gross = Number(item.quantity || 0) * Number(item.fee_amount || 0)
      const discountRate = Number(item.discount_rate || 0)
      const discountAmountInput = Number(item.discount_amount || 0)
      const discount = item.discount_type === '%' ? gross * discountRate / 100 : discountAmountInput
      item.discount_rate = item.discount_type === '%' ? discountRate : 0
      item.discount_amount = Number(discount.toFixed(2))
      item.total_amount = Math.max(gross - item.discount_amount, 0)
      item.amount_include_vat = item.total_amount + Number(item.vat_amount || 0)
    },
    syncTotal(index) {
      const item = this.model.items[index]
      item.total_amount = Number(item.amount_include_vat || 0)
    },
    removeItem(index) {
      this.model.items.splice(index, 1)
    },

    async savePreInvoiceForQr() {
      if (!this.ensureExchangeRateSetup()) return null

      this.errors = new Errors()
      this.khqrBusy = true
      this.model.total_amount = this.totalAmount
      try {
        const payload = this.buildPayload()
        const response = await this.saveForQrRequest(payload)
        notificationAlert(response, 'success')
        const id = response.data && response.data.id ? response.data.id : this.khqrSourceId
        if (!id) {
          this.$notification.warning({
            message: this.$t('preInvoiceV2.khqrSaveFirstFailed'),
            style: { zIndex: 1031 }
          })
          return null
        }
        this.khqrSourceId = id
        return id
      } catch (error) {
        notificationAlert(error)
        if (error.response && error.response.data && error.response.data.errors) {
          this.errors = new Errors(error.response.data.errors)
        }
        return null
      } finally {
        this.khqrBusy = false
      }
    },
    buildPayload() {
      return Object.assign({}, this.model, {
        items: this.model.items.map(item => {
          const row = Object.assign({}, item)
          delete row.feeItems
          return row
        })
      })
    },
    saveForQrRequest(payload) {
      return this.onUpdate(Object.assign({ id: this.invoiceId }, payload))
    },
    async resolveKhqrSourceId() {
      return await this.savePreInvoiceForQr()
    },
    async openPayByQr() {
      if (!this.ensureExchangeRateSetup()) return

      if (this.totalAmount <= 0) {
        this.$notification.warning({
          message: this.$t('preInvoiceV2.khqrNoAmount'),
          style: { zIndex: 1031 }
        })
        return
      }
      this.publishCustomerDisplay()
      this.khqrModalVisible = true
    },
    onKhqrStatusChange(status) {
      this.khqrStatus = status
    },
    onKhqrDisplayChange(payload) {
      this.khqrDisplay = payload
      this.publishCustomerDisplay()
    },
    async onKhqrPaymentSuccess(payload) {
      this.khqrStatus = 'paid'
      this.khqrDisplay = Object.assign({}, this.khqrDisplay || {}, payload || {}, {
        amount: Number((payload && payload.amount) || (this.khqrDisplay && this.khqrDisplay.amount) || this.totalAmount),
        status: 'paid'
      })
      this.khqrModalVisible = false
      this.publishCustomerDisplay()
      this.publishCustomerDisplayPaymentSuccess()

      const route = { name: 'pre-invoice-v2' }
      const id = this.khqrSourceId || this.invoiceId
      if (this.model.preview_pdf && id) {
        await this.$nextTick()
        await this.previewSavedPdf(id, route, true)
      } else {
        this.$router.push(route)
      }
    },
    async save(addNew = false) {
      if (!this.ensureExchangeRateSetup()) return

      this.errors = new Errors()
      this.saving = true
      this.savingAction = addNew ? 'addNew' : 'save'
      this.model.total_amount = this.totalAmount
      try {
        const payload = this.buildPayload()
        const response = await this.onUpdate(Object.assign({ id: this.invoiceId }, payload))
        notificationAlert(response, 'success')
        if (this.model.preview_pdf && response.data && response.data.id) {
          await this.previewSavedPdf(
            response.data.id,
            addNew ? null : { name: 'pre-invoice-v2' }
          )
        }
        if (!this.model.preview_pdf) {
          this.$router.push({ name: 'pre-invoice-v2' })
        }
      } catch (error) {
        notificationAlert(error)
        if (error.response && error.response.data && error.response.data.errors) {
          this.errors = new Errors(error.response.data.errors)
        }
      } finally {
        this.saving = false
        this.savingAction = null
      }
    },
    cancel() {
      this.$router.back()
    },
    resetForm(studentType = 'old', keepExchangeRate = true) {
      const exchangeRateSetup = keepExchangeRate ? this.exchangeRateSetup : null
      this.model = this.freshModel()
      this.khqrSourceId = this.isEdit ? this.invoiceId : null
      this.khqrStatus = null
      this.khqrDisplay = null
      this.model.student_type = studentType
      if (exchangeRateSetup) {
        this.exchangeRateSetup = exchangeRateSetup
        this.model.exchange_rate = exchangeRateSetup.exchange_rate
      } else {
        this.exchangeRateSetup = null
      }
      this.studentSearch = ''
      this.selectedEnrollment = null
      this.subPrograms = []
      this.academicTerms = []
      this.grades = []
      this.feeTypes = []
      this.targetReason = null
      this.errors = new Errors()
    }
  }
}
</script>

<style>

.exchange-rate-alert-wrap .ant-modal-content {
  border-radius: 8px;
  overflow: hidden;
}

.exchange-rate-alert-wrap .ant-modal-body {
  padding: 0;
}

.exchange-rate-alert-wrap .ant-modal-close:focus,
.exchange-rate-alert-wrap .ant-modal-close:active,
.exchange-rate-alert-wrap .ant-modal-close-x:focus,
.exchange-rate-alert-wrap .ant-modal-close-x:active {
  outline: none;
  box-shadow: none;
}

.exchange-rate-alert {
  padding: 28px 28px 24px;
  text-align: center;
  background: #fff;
}

.exchange-rate-alert-icon {
  width: 54px;
  height: 54px;
  margin: 0 auto 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff7ed;
  color: #f97316;
  font-size: 22px;
}

.exchange-rate-alert h3 {
  margin: 0 0 8px;
  font-size: 18px;
  line-height: 1.35;
  font-weight: 700;
  color: #111827;
}

.exchange-rate-alert p {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #4b5563;
}


.pre-invoice-page {
  padding-bottom: 92px;
}

.pre-invoice-shell {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pre-invoice-top-grid {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(320px, 1fr);
  gap: 12px;
  align-items: stretch;
}

.k12-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.k12-panel-stretch {
  display: flex;
  flex-direction: column;
}

.k12-panel-header {
  min-height: 44px;
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.k12-panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
}

.k12-icon {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eff6ff;
  color: #3b82f6;
  font-size: 11px;
  flex: 0 0 auto;
}

.k12-panel-body {
  padding: 14px;
}

@media (max-width: 992px) {
  .pre-invoice-top-grid {
    grid-template-columns: 1fr;
  }
}
</style>
